import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6" aria-label="主导航">
        <div className="mx-auto flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 whitespace-nowrap ${
              scrolled || !isHome ? 'text-foreground-900' : 'text-white'
            }`}
          >
            <span className="w-8 h-8 flex items-center justify-center">
              <i className="ri-book-read-line text-2xl"></i>
            </span>
            <span className="font-heading text-xl font-bold tracking-wide">
              韩国漫画网
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? scrolled || !isHome
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-white/20 text-white'
                  : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-200'
              }`}
            >
              首页
            </Link>
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.includes(`/category/${cat.slug}`)
                    ? 'bg-primary-100 text-primary-700'
                    : scrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-200'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'bg-primary-100 text-primary-700'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-200'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled || !isHome
                  ? 'text-foreground-700 hover:bg-background-200'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg md:hidden transition-colors cursor-pointer ${
                scrolled || !isHome
                  ? 'text-foreground-700 hover:bg-background-200'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              aria-label="菜单"
            >
              <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200/70 py-3 px-4">
            <div className="max-w-2xl mx-auto relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画名称、作者或关键词..."
                className="w-full pl-10 pr-4 py-2.5 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>
        )}

        {mobileOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50">
            <div className="px-4 py-3 flex flex-col gap-1">
              <Link
                to="/"
                className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
                  location.pathname === '/'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                首页
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
                    location.pathname.includes(`/category/${cat.slug}`)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`px-4 py-2.5 rounded-lg text-sm font-medium ${
                  location.pathname === '/about'
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}